import { InventorySettings } from 'components/containers/settings/model/inventorySettingsModel'
import { PurchaseSettings } from 'components/containers/settings/model/purchaseSettingsModel'
import { OverviewSettings } from 'components/containers/settings/model/overviewSettingsModel'
import { ErpSettings } from 'components/containers/settings/model/ledgerSettingsModel'
import { CustomerSettings } from 'components/containers/settings/model/customerSettingsModel'
import { DocumentSettings } from '../model/documentSettingsModel'
import { DISPATCH_TICKET_ENUM, GS_ROLE } from 'utils/constants'
import { AdminSettings } from 'gen/rtk-openapi'
import { defaultAdminSettings } from 'components/containers/settings/utils/defaultAdminSettings'

/**
 * !! IMPORTANT
 * As we add typing for the response model for each individual setting, we can use the type definition
 * from src/gen/rtk-openapi.ts instead of re-defining the type here.
 * This will ensure that BE/FE models are in sync. It is not necessary to define the type here since it is already declared by the BE
 * and published in the OPEN API schema.
 */
export interface GSSettings {
  erpSettings: ErpSettings
  overviewSettings: OverviewSettings
  purchaseSettings: PurchaseSettings
  contractSettings: any // todo
  inventorySettings: InventorySettings
  dispatchSettings: any // todo
  documentSettings: DocumentSettings
  adminSettings: AdminSettings // this model is auto generated via the OPEN API schema
  customerSettings: CustomerSettings
}

export const defaultSettings: GSSettings = {
  erpSettings: {
    general: {
      erpType: undefined,
      autoSyncEnabled: false,
      syncBillsBasedOn: 'effectiveDate',
    },
  },
  documentSettings: {
    general: {
      includeOperatorLastName: false,
      includeAddressInDocumentHeader: true,
      purchaseTicketDisclaimer: '',
      scaleTicketDisclaimer: '',
      packingListDisclaimer: '',
      billOfLadingDisclaimer: '',
      invoiceDisclaimer: '',
      weighmasterCertificate: '',
      documentsWithTimestamps: ['IBT_SCALE_TICKET', 'IBT_PURCHASE_TICKET'],
      documentsWithQRCodes: [],
      logoOnSeparateRow: false,
    },
  },
  overviewSettings: {
    overview: {
      lockAccountingPeriod: {
        lockAccountingDate: undefined,
        isLockedForAccounting: false,
      },
    },
  },
  purchaseSettings: {
    deductions: {
      listOfDeductions: [
        {
          active: true,
          display: 'Water',
          id: '1',
          isNew: false,
          report: 'Water',
        },
        {
          active: true,
          display: 'Trash',
          id: '2',
          isNew: false,
          report: 'Trash',
        },
        {
          active: true,
          display: 'Unsorted',
          id: '3',
          isNew: false,
          report: 'Unsorted',
        },
      ],
    },
    inboundTickets: {
      fieldVisibility: ['truckNumber', 'notes'],
      addCustomersOnInboundTickets: false,
      useDefaultRetailCustomer: false,
      enableAutosave: false,
      inboundTicketMaterialFields: [],
      decimalPrecision: 2,
      thresholdAmount: '',
      thresholdUM: '',
      showWeightDecimals: false,
      flagManuallyEnteredWeights: false,
      autoSaveAndCloseAfterPrintingReceipt: false,
      autoSaveAndCloseAfterPrintingScaleReceipt: false,
      disableUmDropdowns: false,
      enableAxleWeights: false,
      defaultNumberOfAxles: 2,
    },
    payment: {
      automaticallyIncrementCheckNumber: false,
      startingCheckNumber: '00001',
      roundCashPayments: '0',
      roundingDirection: 'NEAREST',
      roundOnlyATMandCash: false,
      yardPreferredPaymentMethod: 'cash',
      reuseCustomerFingerprint: false,
      mreEntityId: '',
      representativeName: '',
      representativeSignatureImage: '',
      checkSignatureImage: '',
      paymentThreshold: {
        hasThreshold: false,
        ticketValueThreshold: '',
        allowedPaymentTypes: [],
      },
      highBalancePaymentAuthorization: {
        hasThreshold: false,
        ticketValueThreshold: null,
        roleBasedThresholds: [
          { id: GS_ROLE.YARD_MANAGER, enabled: false, threshold: '' },
          { id: GS_ROLE.SCALE_OPERATOR, enabled: false, threshold: '' },
          { id: GS_ROLE.YARD_EMPLOYEE, enabled: false, threshold: '' },
        ],
      },
      checkLayout: {
        paymentDate: {
          top: 49,
          right: 15,
        },
        checkRecipient: {
          top: 98,
          left: 90,
        },
        amount: {
          top: 99,
          right: 20,
        },
        textAmount: {
          top: 130,
          left: 15,
        },
        customerAddress: {
          top: 170,
          left: 90,
        },
        memo: {
          top: 235,
          left: 60,
        },
        checkSignature: {
          top: 178,
          left: 545,
        },
      },
      commodityType: {
        ferrousCostThreshold: {
          hasThreshold: false,
          ticketValueThreshold: '',
          allowedPaymentTypes: [],
        },
        nonFerrousCostThreshold: {
          hasThreshold: false,
          ticketValueThreshold: '',
          allowedPaymentTypes: [],
        },
      },
      requiredInYardPaymentFields: [],
    },
    scaleView: {
      customizedMaterialFields: [],
      enableScaleIntegration: true,
    },
  },
  contractSettings: {
    autoCloseContracts: {
      autoCloseSetting: 'off',
      warningSetting: 'off',
      fulfillmentWarningThreshold: 90,
      expirationWarningThreshold: 4,
    },
    purchaseOrders: {
      purchaseOrderDisclaimer: '',
    },
    salesOrders: {
      salesOrderDisclaimer: '',
    },
  },
  inventorySettings: {
    general: {
      allowNegativeInventory: false,
    },
    finishedGoods: {
      listOfFinishedGoods: [
        {
          active: true,
          display: 'Bale',
          id: '4',
        },
        {
          active: true,
          display: 'Box',
          id: '5',
        },
        {
          active: true,
          display: 'Pallet',
          id: '6',
        },
        {
          active: true,
          display: 'Other',
          id: '7',
        },
      ],
    },
  },
  dispatchSettings: {
    loadBoard: {
      showPastLoadBoard: false,
      defaultTripType: DISPATCH_TICKET_ENUM.PICK_UP,
      defaultSchedulingOffset: 0,
      supplierTypesFilter: [],
    },
  },
  customerSettings: {
    compliance: {
      enableRaceAndWeightCollection: false,
    },
  },
  adminSettings: defaultAdminSettings,
}
